import { Component, OnInit } from '@angular/core';
import { AsyncPipe, Location, NgClass, NgForOf, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  throwError,
} from 'rxjs';
import { MainButtonComponent } from '../../shared/ui/main-button/main-button.component';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { DateTime } from 'luxon';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  FormBuilder,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LockerApiService } from '../../api/locker-api.service';
import {
  GetLockerHistoryOutput,
  UpdateLockerInput,
} from '../../api/locker-api.types';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { PersonApiService } from '../../api/person-api.service';
import { Person } from '../../api/person-api.class';
import { NzMessageService } from 'ng-zorro-antd/message';
import { StoreService } from '../../store/store.service';
import { Locker } from '../../api/locker-api.class';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { ProvisionRequestApiService } from '../../api/provision-request-api.service';
import { CreateProvisionRequestInput } from '../../api/provision-request-api.types';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import {
  NzDropDownADirective,
  NzDropDownDirective,
  NzDropdownMenuComponent,
} from 'ng-zorro-antd/dropdown';
import { NzMenuDirective, NzMenuItemComponent } from 'ng-zorro-antd/menu';
import { FormTextareaComponent } from '../../shared/ui/form-textarea/form-textarea.component';
import { FormSelectComponent } from '../../shared/ui/form-select/form-select.component';
import { FormInputComponent } from '../../shared/ui/form-input/form-input.component';

function isNonNullLocker(locker: Locker | null): locker is Locker {
  return !!locker;
}

@Component({
  selector: 'app-locker-detail',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MainButtonComponent,
    NgIf,
    NgClass,
    NgForOf,
    NzIconModule,
    NzAutocompleteModule,
    NzDatePickerModule,
    NzSpinModule,
    NzInputDirective,
    NzInputGroupComponent,
    NzSelectModule,
    NzModalModule,
    NzTimelineModule,
    ReactiveFormsModule,
    NzCollapseModule,
    NzDropdownMenuComponent,
    NzMenuDirective,
    NzMenuItemComponent,
    NzDropDownADirective,
    NzDropDownDirective,
    FormTextareaComponent,
    FormSelectComponent,
    FormInputComponent,
  ],
  templateUrl: './locker-detail.component.html',
  styleUrl: './locker-detail.component.scss',
})
export class LockerDetailComponent implements OnInit {
  private searchSubject = new Subject<string>();
  locker$: Observable<Locker>;
  lockerHistory!: GetLockerHistoryOutput;
  searchedPersons$?: Observable<Person[]>;
  editMode = false;
  isLoading = false;
  modalIsVisible = false;
  searchTerm?: string;
  date: Date[] = [];
  selectSizes = [
    { valueKey: 1, labelKey: 'Schmal (300mm)' },
    { valueKey: 2, labelKey: 'Breit (400mm)' },
    {
      valueKey: 3,
      labelKey: 'Doppelspind',
    },
    { valueKey: 4, labelKey: 'Kleiderfach' },
    { valueKey: 5, labelKey: 'Helmfach' },
    { valueKey: 6, labelKey: 'Sammelspind' },
  ];
  selectLocation = [
    { valueKey: 1, labelKey: 'B835' },
    { valueKey: 2, labelKey: 'B836' },
    {
      valueKey: 3,
      labelKey: 'B845',
    },
  ];
  selectGender = [
    { valueKey: 1, labelKey: 'Männlich' },
    { valueKey: 2, labelKey: 'Weiblich' },
    {
      valueKey: 3,
      labelKey: 'Divers',
    },
  ];
  selectStatus = [
    { valueKey: 1, labelKey: 'Frei' },
    { valueKey: 2, labelKey: 'Belegt' },
    {
      valueKey: 3,
      labelKey: 'Gesperrt',
    },
  ];

  get allowedStatusOptions() {
    // If not in edit mode, show all options (for display)
    if (!this.editMode) {
      return this.selectStatus;
    }

    // In edit mode: only allow "Frei" (1) and "Gesperrt" (3)
    // "Belegt" (2) can only be set via "Zuweisen" action
    return this.selectStatus.filter(
      (status) => status.valueKey === 1 || status.valueKey === 3,
    );
  }
  selectReasonForBlock = [
    { valueKey: 1, labelKey: 'Beschädigt' },
    { valueKey: 2, labelKey: 'Verschmutzt' },
    {
      valueKey: 3,
      labelKey: 'Schloss fehlt',
    },
    {
      valueKey: 4,
      labelKey: 'Schlüssel fehlt',
    },
    {
      valueKey: 5,
      labelKey: 'Sonstiger Sperrgrund',
    },
  ];

  validateLockerForm = this.fb.group({
    optionalFields: this.fb.group({
      sperrgrund: this.fb.control<number | null>(null),
      bemerkung: this.fb.control<string | null>(null),
      sperrkennzeichen: this.fb.control<number | null>(null),
      schluessel_ausgegeben_am: this.fb.control<string | null>(null),
      zweitschluessel_ausgegeben_am: this.fb.control<string | null>(null),
      person: this.fb.control<string | null>(null),
    }),
    mandatoryFields: this.nonNullableFb.group({
      bezeichnung: this.nonNullableFb.control('', [Validators.required]),
      spindart: this.nonNullableFb.control(0, [Validators.required]),
      geschlecht: this.nonNullableFb.control(0, [Validators.required]),
      status: this.nonNullableFb.control(0, [Validators.required]),
      standort: this.nonNullableFb.control(0, [Validators.required]),
    }),
  });

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private nonNullableFb: NonNullableFormBuilder,
    private lockerApiService: LockerApiService,
    private personApiService: PersonApiService,
    private provisionApiService: ProvisionRequestApiService,
    private modal: NzModalService,
    private message: NzMessageService,
    private store: StoreService,
    private location: Location,
  ) {
    this.locker$ = this.store
      .select((state) => state.lockers.activeLocker)
      .pipe(filter(isNonNullLocker));
    this.lockerHistory = this.route.snapshot.data['lockerHistory'].reverse();
  }

  async ngOnInit() {
    await this.prefillForm();

    this.searchedPersons$ = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((searchTerm) => searchTerm.length >= 2),
      switchMap((searchTerm) => {
        this.isLoading = true;
        return this.personApiService.searchPerson({ name: searchTerm }).pipe(
          map((response) => Person.fromResultArray(response)),
          catchError((err) => {
            if (err.status === 404) {
              return of([]);
            } else {
              return throwError(() => err);
            }
          }),
          finalize(() => {
            this.isLoading = false;
          }),
        );
      }),
    );
  }

  async navigateBack() {
    // await this.router.navigate(['lockers']);
    this.location.back();
  }

  async prefillForm() {
    const locker = await firstValueFrom(this.locker$);
    this.validateLockerForm.get('mandatoryFields')?.patchValue({
      bezeichnung: locker.bezeichnung,
      spindart: locker.getLockerOutput.spindart,
      status: locker.getLockerOutput.status,
      geschlecht: locker.getLockerOutput.geschlecht,
      standort: locker.getLockerOutput.standort,
    });

    this.validateLockerForm.get('optionalFields')?.patchValue({
      sperrgrund: locker.getLockerOutput.sperrgrund ?? null,
      bemerkung: locker.bemerkung ?? '',
      sperrkennzeichen: locker.getLockerOutput.sperrkennzeichen ?? null,
      schluessel_ausgegeben_am:
        locker.getLockerOutput.schluessel_ausgegeben_am ?? null,
      zweitschluessel_ausgegeben_am:
        locker.getLockerOutput.zweitschluessel_ausgegeben_am ?? null,
      person:
        locker.zugewiesenAn?.vorname +
        ' ' +
        locker.zugewiesenAn?.name +
        ' (Gruppe: ' +
        (locker.zugewiesenAn?.gruppe ?? 'Keine') +
        ')',
    });
  }

  async updateLocker(lockerId: number) {
    if (this.validateLockerForm.valid) {
      const form = this.validateLockerForm;
      const bemerkung = form.get('optionalFields.bemerkung');

      // Check if only "Bemerkung" has been changed
      const otherFieldsDirty =
        form.get('mandatoryFields')?.dirty ||
        form.get('optionalFields.sperrgrund')?.dirty ||
        form.get('optionalFields.sperrkennzeichen')?.dirty ||
        form.get('optionalFields.schluessel_ausgegeben_am')?.dirty ||
        form.get('optionalFields.zweitschluessel_ausgegeben_am')?.dirty ||
        form.get('optionalFields.person')?.dirty;

      const onlyBemerkungChanged = bemerkung?.dirty && !otherFieldsDirty;

      const mandatoryValues = this.validateLockerForm
        .get('mandatoryFields')
        ?.getRawValue();
      const optionalValues = this.validateLockerForm
        .get('optionalFields')
        ?.getRawValue();

      const locker: UpdateLockerInput = {
        ...mandatoryValues,
        ...optionalValues,
      };
      const originalLocker = await firstValueFrom(this.locker$);
      // Save only if bemerkung has changed or status is not 2 (frei)
      if (onlyBemerkungChanged || originalLocker.status !== 'Belegt') {
        const {
          lockers: { setActiveLocker },
        } = this.store.getActions();
        await this.lockerApiService.updateLocker(lockerId, locker);
        await setActiveLocker(lockerId);
        await this.prefillForm();
        this.message.success('Änderungen erfolgreich gespeichert.');
        this.validateLockerForm.markAsPristine();
      } else {
        this.message.warning(
          'Bitte geben Sie den Spind zuerst frei, bevor Sie ihn sperren.',
        );
        this.validateLockerForm.markAsPristine();
      }
    }
  }

  async deleteLocker() {
    const locker = await firstValueFrom(this.locker$);
    this.modal.confirm({
      nzTitle: 'Bestätigung der Löschung',
      nzContent:
        '<b style="color: red;">Sind Sie sicher, dass Sie diesen Spind löschen möchten?</b>',
      nzOkText: 'Ja',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: async () => {
        if (locker.person) {
          await this.handleClosingProvisionRequest(locker.person);
          const {
            lockers: { releaseLocker },
          } = this.store.getActions();
          releaseLocker(locker.id);
          await this.lockerApiService.deleteLocker(locker.id);
        } else {
          await this.lockerApiService.deleteLocker(locker.id);
        }
        this.navigateBack();
      },
      nzCancelText: 'Nein',
      nzOnCancel: () => console.log('Cancel'),
    });
  }

  async switchEditMode() {
    await this.prefillForm();
    this.editMode = !this.editMode;
  }

  async saveLocker() {
    const locker = await firstValueFrom(this.locker$);
    this.modal.confirm({
      nzTitle: 'Möchten Sie die Änderungen speichern?',
      nzContent:
        '<b style="color: red;">Klicken Sie auf "Nein" um das Bearbeiten abzubrechen.</b>',
      nzOkText: 'Ja',
      nzOkType: 'primary',
      nzOkDanger: false,
      nzOnOk: () => {
        this.editMode = !this.editMode;
        if (this.locker$) {
          this.updateLocker(locker.id);
        }
      },
      nzCancelText: 'Nein',
      nzOnCancel: () => {
        this.editMode = !this.editMode;
      },
    });
  }

  onAssignClick() {
    this.modalIsVisible = true;
  }

  async onLockerReleaseClick() {
    const locker = await firstValueFrom(this.locker$);
    const {
      lockers: { releaseLocker },
    } = this.store.getActions();
    this.modal.confirm({
      nzTitle: 'Schlüsselrückgabe',
      nzContent:
        '<b style="color: red;">Klicken Sie auf "Ja" um die Schlüsselrückgabe abzuschließen und den Spind wieder freizugeben. ' +
        'Klicken Sie auf "Nein" um abzubrechen.</b>',
      nzOkText: 'Ja',
      nzOkType: 'primary',
      nzOkDanger: false,
      nzOnOk: async () => {
        if (this.locker$) {
          await this.confirmDeleteBemerkung(locker);
          releaseLocker(locker.id);
          await this.handleClosingProvisionRequest(locker.person);
          await this.refreshLockerHistory();
          this.prefillForm();
        }
      },
      nzCancelText: 'Nein',
    });
  }

  handleCancel() {
    this.modalIsVisible = false;
    this.searchTerm = '';
  }

  async handleOk() {
    const locker = await firstValueFrom(this.locker$);
    const spindpersonId = parseInt(this.searchTerm ? this.searchTerm : '');
    const person = await firstValueFrom(
      this.personApiService.getPerson(spindpersonId),
    );

    // Check if person already has a locker of type 1, 2, or 3
    const alreadyHasMainLocker = person.spinde.some(
      (locker) =>
        locker.spindartId === 1 ||
        locker.spindartId === 2 ||
        locker.spindartId === 3,
    );

    const isMainLocker =
      locker.rawSpindart === 1 ||
      locker.rawSpindart === 2 ||
      locker.rawSpindart === 3;

    const triesToAddDupMainLocker = alreadyHasMainLocker && isMainLocker;
    if (!triesToAddDupMainLocker) {
      // Check if person already has a locker with the same type as the new locker
      const duplicateLocker = person.spinde.find(
        (dLocker) => dLocker.spindartId === locker.rawSpindart,
      );

      if (duplicateLocker) {
        this.message.warning(
          `Spinde der Kategorie ${locker.spindart} können nicht mehrfach zugewiesen werden.`,
        );
        return;
      }

      const personName = `${person.vorname} ${person.name}`;
      const {
        lockers: { assignLocker },
      } = this.store.getActions();

      // Check if the selected start date is in the past
      if (
        DateTime.fromJSDate(this.date[0]).startOf('day') <
        DateTime.now().startOf('day')
      ) {
        this.message.warning(
          'Das Startdatum darf nicht in der Vergangenheit liegen.',
        );
        return;
      }

      if (this.date[0] && this.date[1]) {
        const requestInput: CreateProvisionRequestInput = {
          bezeichnung: `Einzelausgabe Spind ${personName}`,
          datum_von: this.date[0].toISOString(),
          datum_bis: this.date[1].toISOString(),
          modulId: 0,
          kleidungsart: 'Keine Angabe',
          spindart: locker.getLockerOutput.spindart,
          bemerkung: `Einzelausgabe Spind, PersonenId: ${spindpersonId}`,
          spindstandort: locker.getLockerOutput.standort,
          personen: [spindpersonId],
        };

        if (!isNaN(spindpersonId)) {
          await this.provisionApiService.createProvisionRequest(requestInput);
          await this.confirmDeleteBemerkung(locker);
          await assignLocker(locker.id, { spindpersonId });
          await this.refreshLockerHistory();
          this.prefillForm();
          this.modalIsVisible = false;
        } else {
          this.message.warning('Bitte eine vorgeschlagene Person auswählen');
        }
      }
    } else {
      this.message.warning(
        'Dieser Person ist bereits ein Hauptspind (schmal, breit, doppelt) zugewiesen. ' +
          'Bitte geben Sie diesen erst frei.',
        { nzDuration: 5000 },
      );
    }
  }

  formatDate(date: string) {
    return DateTime.fromISO(date).toFormat('D', { locale: 'de' });
  }

  onInput() {
    if (this.searchTerm) this.searchSubject.next(this.searchTerm);
  }

  setHistoryColor(event: string | undefined) {
    switch (event) {
      case (event = 'Spind freigegeben'):
        return 'green';
      case (event = 'Spind neu zugewiesen'):
        return 'red';
      default:
        return 'blue';
    }
  }

  async onReplaceLockClick() {
    const locker = await firstValueFrom(this.locker$);
    const {
      lockers: { replaceLock },
    } = this.store.getActions();
    this.modal.confirm({
      nzTitle: 'Schloss ersetzen',
      nzContent:
        '<b style="color: red;">Bitte bestätigen Sie, ' +
        'dass das Schloss ersetzt wurde.</b>',
      nzOkText: 'Bestätigen',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: async () => {
        if (this.locker$) {
          await replaceLock(locker.id);
          await this.handleClosingProvisionRequest(locker.person);
          await this.refreshLockerHistory();
          this.prefillForm();
        }
      },
      nzCancelText: 'Abbrechen',
    });
  }

  async onHandoutSecondKey() {
    const locker = await firstValueFrom(this.locker$);
    const {
      lockers: { setActiveLocker },
    } = this.store.getActions();
    if (locker) {
      try {
        await this.lockerApiService.handoutLockerKey(locker.id, {
          ausgabedatum: DateTime.now().toISODate(),
        });
        await setActiveLocker(locker.id);
        await this.refreshLockerHistory();
        this.message.success(
          'Ein zweiter Schlüssel wurde erfolgreich ausgegeben.',
        );
      } catch {
        this.message.error(
          'Es kann kein weiterer Schlüssel mehr ausgeben werden.',
        );
      }
    }
  }

  async onSecondKeyWithdrawalClick() {
    const locker = await firstValueFrom(this.locker$);
    const {
      lockers: { setActiveLocker },
    } = this.store.getActions();

    if (locker) {
      try {
        await this.lockerApiService.withdrawLockerKey(locker.id, {
          ruecknahmedatum: DateTime.now().toISODate(),
        });
        await setActiveLocker(locker.id);
        await this.refreshLockerHistory();
        this.message.success(
          'Zweitschlüssel wurde erfolgreich zurückgenommen.',
        );
      } catch {
        this.message.error(
          'Etwas bei der Rücknahme des Zweitschlüssels ist schief gelaufen.',
        );
      }
    }
  }

  async refreshLockerHistory() {
    const locker = await firstValueFrom(this.locker$);
    this.lockerHistory = [
      ...(await this.lockerApiService.getLockerHistory(locker.id)),
    ].reverse();
  }

  async handleClosingProvisionRequest(userId: number) {
    const provisionRequests = await firstValueFrom(
      this.provisionApiService.getAllProvisionRequests({}),
    );

    const closeProvision = provisionRequests.find(
      (request) =>
        request.bemerkung === `Einzelausgabe Spind, PersonenId: ${userId}`,
    );

    if (closeProvision) {
      this.provisionApiService.closeProvisionRequest(closeProvision.id, {
        beendet_am: DateTime.now().toISODate(),
      });
    }
  }

  getPersonGroup(person: any): string {
    return person.gruppe ? person.gruppe : 'Keine';
  }

  private async confirmDeleteBemerkung(locker: Locker) {
    if (!locker.bemerkung || locker.bemerkung.trim().length === 0) return;

    const confirmed = await new Promise<boolean>((resolve) => {
      this.modal.confirm({
        nzTitle: 'Bemerkung löschen?',
        nzContent: `<b>Der Spind enthält aktuell eine Bemerkung:</b><br><i>"${locker.bemerkung}"</i><br><br>Möchten Sie diese löschen?`,
        nzOkText: 'Ja, löschen',
        nzCancelText: 'Nein, behalten',
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false),
      });
    });

    if (confirmed) {
      const mandatoryValues = this.validateLockerForm
        .get('mandatoryFields')
        ?.getRawValue();
      const optionalValues = this.validateLockerForm
        .get('optionalFields')
        ?.getRawValue();

      const updateLocker: UpdateLockerInput = {
        ...mandatoryValues,
        ...optionalValues,
      };
      updateLocker.bemerkung = '';
      await this.lockerApiService.updateLocker(locker.id, updateLocker);
      this.message.info('Die Bemerkung wurde gelöscht.');
    }
  }
}
